/**
 * AssetClassFilter Component
 * TASK-207: Portfolio Level View - Enhanced Filters
 *
 * Multi-select button group for asset class filtering
 * Allows selection of multiple asset classes to narrow portfolio view
 */

import React from 'react';
import { AssetClassFilter as AssetClassFilterType } from '../../../types/portfolio-views';

interface AssetClassFilterProps {
  selected: AssetClassFilterType[];
  onChange: (selected: AssetClassFilterType[]) => void;
  disabled?: boolean;
}

const AssetClassFilter: React.FC<AssetClassFilterProps> = ({
  selected,
  onChange,
  disabled = false,
}) => {
  const assetClasses: { value: AssetClassFilterType; label: string; color: string }[] = [
    { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-800' },
    { value: 'equities', label: 'Equities', color: 'bg-blue-100 text-blue-800' },
    { value: 'bonds', label: 'Bonds', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'funds', label: 'Funds', color: 'bg-green-100 text-green-800' },
    { value: 'derivatives', label: 'Derivatives', color: 'bg-purple-100 text-purple-800' },
    { value: 'cash', label: 'Cash', color: 'bg-gray-100 text-gray-800' },
    { value: 'alternative', label: 'Alternative', color: 'bg-orange-100 text-orange-800' },
  ];

  const handleToggle = (assetClass: AssetClassFilterType) => {
    if (disabled) return;

    if (assetClass === 'all') {
      // Clicking "All" resets to all selected
      onChange(['all']);
    } else {
      // Remove 'all' from current selection
      const current = selected.filter((s) => s !== 'all');
      const isSelected = current.includes(assetClass);

      if (isSelected) {
        // Deselect this asset class
        const newSelected = current.filter((s) => s !== assetClass);
        // If nothing selected, default to 'all'
        onChange(newSelected.length > 0 ? newSelected : ['all']);
      } else {
        // Add this asset class to selection
        onChange([...current, assetClass]);
      }
    }
  };

  const isSelected = (assetClass: AssetClassFilterType): boolean => {
    if (selected.includes('all')) {
      return assetClass === 'all';
    }
    return selected.includes(assetClass);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Asset Class
      </label>
      <div className="flex flex-wrap gap-2">
        {assetClasses.map((assetClass) => {
          const selected = isSelected(assetClass.value);

          return (
            <button
              key={assetClass.value}
              type="button"
              onClick={() => handleToggle(assetClass.value)}
              disabled={disabled}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium
                border transition-all duration-200
                ${
                  selected
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {assetClass.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">
        {selected.includes('all')
          ? 'Showing all asset classes'
          : `Showing ${selected.length} asset ${selected.length === 1 ? 'class' : 'classes'}`}
      </p>
    </div>
  );
};

export default AssetClassFilter;
